import { getRedirectResult, getIdToken, signOut } from 'firebase/auth';
import { auth, getAuthMethod } from './firebase-config';
import { getCurrentEnvironmentConfig, isPreviewChannelHostname } from '@/config/environment-config'; // pragma: allowlist secret
import { completeLogin } from '@/lib/access/client';

// Store the redirect result globally so it can be accessed by AuthContext
let pendingRedirectResult: any = null;
let redirectResultChecked = false;
// Global flag to prevent multiple module-level executions
let moduleInitialized = false;

// Track redirect attempts for better debugging
const REDIRECT_STORAGE_KEY = 'firebase_redirect_intent';
const REDIRECT_TIME_KEY = 'firebase_redirect_time';

// Debug only in development; never log OAuth flow details in production
const DEBUG = typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;

function debugLog(...args: any[]) {
  if (DEBUG) {
    logger.debug(`[🔍 auth-redirect-handler] ${new Date().toISOString()} ${args.map((arg) => {
      if (typeof arg === 'string') return arg;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    }).join(' ')}`);
  }
}
import logger from '@/lib/logging';

/**
 * Set redirect intent before initiating OAuth
 * This helps track if a redirect was expected but failed
 */
export function setRedirectIntent() {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(REDIRECT_STORAGE_KEY, 'google');
    sessionStorage.setItem(REDIRECT_TIME_KEY, Date.now().toString());
    debugLog('Redirect intent set for Google OAuth');
  }
}

/**
 * Clear redirect intent after successful authentication or timeout
 */
export function clearRedirectIntent() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
    sessionStorage.removeItem(REDIRECT_TIME_KEY);
    debugLog('Redirect intent cleared');
  }
}

function hasPendingRedirectIntent(): boolean {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(REDIRECT_STORAGE_KEY) === 'google';
}

/**
 * Check if there was a recent redirect attempt that might have failed
 */
function checkForFailedRedirect(): boolean {
  if (typeof window === 'undefined') return false;

  const intent = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
  const redirectTime = sessionStorage.getItem(REDIRECT_TIME_KEY);

  if (intent && redirectTime) {
    const elapsed = Date.now() - parseInt(redirectTime);
    // If redirect was initiated less than 30 seconds ago but we have no result
    if (elapsed < 30000) {
      debugLog('⚠️ Detected recent redirect attempt without result');
      debugLog(`Intent: ${intent}, Time elapsed: ${elapsed}ms`);
      return true;
    } else {
      // Old redirect intent, clear it
      clearRedirectIntent();
    }
  }

  return false;
}

/**
 * Check for OAuth redirect result immediately when the script loads
 * This runs BEFORE React initialization to capture the redirect result
 */
export async function checkForRedirectResult() {
  debugLog('checkForRedirectResult called');
  debugLog('redirectResultChecked:', redirectResultChecked);
  debugLog('typeof window:', typeof window);
  debugLog('window.location.href:', typeof window !== 'undefined' ? window.location.href : 'N/A');

  if (typeof window === 'undefined') {
    debugLog('Skipping - not in browser');
    return pendingRedirectResult;
  }

  // Another caller is already running getRedirectResult — wait for it
  if (redirectResultChecked && redirectCheckPromise) {
    debugLog('Redirect check in progress — awaiting existing promise');
    await redirectCheckPromise;
    return pendingRedirectResult;
  }

  if (redirectResultChecked) {
    debugLog('Skipping - redirect already checked');
    return pendingRedirectResult;
  }

  redirectResultChecked = true;

  // Popup is the default on localhost, but mobile local dev uses redirect — still
  // process the return trip when we have a pending redirect intent in sessionStorage.
  const authMethod = getAuthMethod();
  // Preview channels use popup. Drop leftover redirect intent from older preview
  // builds so a prior failed signInWithRedirect does not surface as redirect_failed.
  if (
    authMethod === 'popup' &&
    hasPendingRedirectIntent() &&
    isPreviewChannelHostname(window.location.hostname)
  ) {
    debugLog('ℹ️ Preview popup auth — clearing leftover redirect intent');
    clearRedirectIntent();
  }
  if (authMethod === 'popup' && !hasPendingRedirectIntent()) {
    debugLog('ℹ️ Auth method is popup and no redirect intent — skipping getRedirectResult');
    return null;
  }
  if (authMethod === 'popup' && hasPendingRedirectIntent()) {
    debugLog('ℹ️ Auth method is popup but redirect intent present — processing getRedirectResult');
  }

  try {
    debugLog('Calling getRedirectResult...');
    debugLog('Auth config:', {
      authDomain: (auth as any).config?.authDomain,
      projectId: (auth as any).config?.projectId,
      currentOrigin: window.location.origin,
      currentHost: window.location.hostname
    });
    const startTime = Date.now();
    const result = await getRedirectResult(auth as any);
    const elapsed = Date.now() - startTime;
    debugLog(`getRedirectResult completed in ${elapsed}ms`);
    debugLog('Result:', result ? 'User found' : 'null');

    if (result && result.user) {
      debugLog('✅ OAuth redirect captured!');
      debugLog('User UID:', result.user.uid);
      debugLog('User email:', result.user.email);
      debugLog('User displayName:', result.user.displayName);
      debugLog('Provider:', result.providerId);
      pendingRedirectResult = result;

      // Server completeLogin upserts profile and mints the session cookie
      try {
        debugLog('Getting ID token...');
        const idToken = await getIdToken(result.user);
        debugLog('ID token obtained, length:', idToken.length);

        debugLog('Sending login request to /api/auth/login...');
        const response = await completeLogin(idToken);

        debugLog('Login response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          debugLog('✅ Session created successfully');
          debugLog('Session user:', data.user);
        } else if (response.status === 403) {
          const data = await response.json().catch(() => ({}));
          const { classifyLogin403Body, rememberStaleSessionOnLogin } = await import(
            '@/lib/login-user-messages'
          );
          const action = classifyLogin403Body(data);
          if (action.kind === 'stay-on-login') {
            logger.warn(
              '🚫 [auth-redirect-handler] Login needs a fresh sign-in (stale auth_time); staying on /login'
            );
            rememberStaleSessionOnLogin();
            if (auth) {
              await signOut(auth);
            }
          } else {
            const redirectTo = action.path;
            logger.warn(
              `🚫 [auth-redirect-handler] Login denied (403): ${data.message || 'Not invited'}, redirecting to ${redirectTo}`
            );
            if (typeof window !== 'undefined' && window.location.pathname !== redirectTo) {
              window.location.assign(redirectTo);
            }
          }
        } else {
          const errorText = await response.text();
          debugLog('❌ Failed to create session');
          debugLog('Error response:', errorText);
          logger.error('❌ [auth-redirect-handler] Failed to create session:', errorText);
        }
      } catch (error) {
        debugLog('❌ Error creating session:', error);
        logger.error('❌ [auth-redirect-handler] Error creating session:', error);
      }
    } else {
      // Check if there was a recent redirect attempt that failed
      const failedRedirect = checkForFailedRedirect();

      if (failedRedirect) {
        debugLog('❌ OAuth redirect likely failed');
        debugLog('Possible causes:');
        debugLog('1. OAuth redirect URIs not configured in Google Cloud Console');
        const envConfig = getCurrentEnvironmentConfig();
        debugLog(`2. Missing: https://${envConfig.firebaseProject}.firebaseapp.com/__/auth/handler`);
        debugLog('3. Browser blocked third-party cookies');
        debugLog('4. Firebase authorized domains not configured');

        // Store error for UI to display
        pendingRedirectResult = {
          error: 'redirect_failed',
          message: 'Google sign-in failed. This is usually a configuration issue. Please contact support or check the browser console for details.'
        };

        // Clear the failed intent
        clearRedirectIntent();
      } else {
        debugLog('ℹ️ No OAuth redirect pending');
        debugLog('This is normal for regular page loads');
      }
    }
  } catch (error: any) {
    debugLog('❌ Error checking redirect:', error);
    debugLog('Error code:', error?.code);
    debugLog('Error message:', error?.message);

    // Handle specific error cases
    if (error?.code === 'auth/invalid-continue-uri') {
      const envConfig = getCurrentEnvironmentConfig();
      debugLog(`⚠️ Invalid continue URI error - this is expected on ${envConfig.firebaseProject}`);
      debugLog('This domain requires popup auth instead of redirect');
      // Don't treat this as a fatal error - just clear any pending redirect
      clearRedirectIntent();
      pendingRedirectResult = null;
    } else {
      logger.error('❌ [auth-redirect-handler] Error checking redirect:', error);
      // Store error for other cases
      pendingRedirectResult = {
        error: error.code || 'redirect_error',
        message: error.message || 'Failed to check redirect result'
      };
    }
  }

  debugLog('Returning pendingRedirectResult:', pendingRedirectResult ? 'User found' : 'null');
  return pendingRedirectResult;
}

/**
 * Get the cached redirect result (if any)
 * This can be called by AuthContext to get the result without re-checking
 */
export function getCachedRedirectResult() {
  return pendingRedirectResult;
}

/**
 * Clear the cached redirect result after it's been handled
 */
export function clearRedirectResult() {
  pendingRedirectResult = null;
}

// Single in-flight promise — all callers await the same redirect check
let redirectCheckPromise: Promise<unknown> | null = null;

/**
 * Get a promise that resolves when the OAuth redirect check has completed.
 * Entry-client must await this before rendering so AuthContext sees the result.
 */
export function getRedirectCheckPromise(): Promise<unknown> {
  if (!redirectCheckPromise) {
    redirectCheckPromise =
      typeof window !== 'undefined'
        ? checkForRedirectResult().catch((error) => {
          debugLog('❌ Error in checkForRedirectResult:', error);
        })
        : Promise.resolve();
  }
  return redirectCheckPromise;
}

// Kick off redirect check as soon as this module loads (entry-client awaits the same promise)
if (typeof window !== 'undefined' && !moduleInitialized) {
  moduleInitialized = true;
  debugLog('=== auth-redirect-handler module loaded ===');
  debugLog('Window location:', window.location.href);
  getRedirectCheckPromise();
}
